import { PartItemFieldOption } from '@/core/domain/stock/Configuration/Configuration.types';
import {
  ListStockItemsQuery,
  StockItem,
  StockItemType,
  StockOrderColumnConfig,
} from '@/core/domain/stock/StockItems/StockItems.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { Alert, Checkbox, Modal, ModalProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table/interface';
import { SorterResult } from 'antd/lib/table/interface';
import {
  useStockItems,
  useStockItemsMutation,
} from '@/core/domain/stock/StockItems/StockItems.hooks';
import { tableSorterOrder } from '@/utils/table';
import InfiniteTable from '../InfiniteTable';
import AppPageHeader from '../AppPageHeader';
import PartsExtraFilters from '@/components/Operational/Inventory/Parts/PartsExtraFilters';
import { useDebounce } from '@/hooks';
import Group from '../Group';
import ModalLoadingOverlay from '../ModalLoadingOverlay';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';

interface Props extends ModalProps {
  onCancel: () => void;
  open: boolean;
  onCallback: (items: StockItem[], all: boolean) => void;
  itemSelectedIds: number[];
  allSelecteds?: boolean;
  overlayMessages?: string[];
  disableWithChecked?: boolean;
  enableAllSelects?: boolean;
  queryParams?: ListStockItemsQuery;
}

const SelectPartsListModal: React.FC<Props> = ({
  onCancel,
  open,
  onCallback,
  itemSelectedIds,
  allSelecteds: allParts = false,
  enableAllSelects = false,
  overlayMessages,
  disableWithChecked = true,
  queryParams: _queryParams,
  ...props
}) => {
  const [selectItems, setSelectItems] = useState<StockItem[]>([]);

  const [allSelecteds, setAllSelecteds] = useState(allParts);

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    itemType: StockItemType.Part,
    order: 'ASC',
    active: true,
  });

  const queryDebounce = useDebounce(queryParams, 1000);

  const listStockItems = useStockItemsMutation();

  const { data, isFetching, hasNextPage, fetchNextPage, dataUpdatedAt } =
    useStockItems(open, { ..._queryParams, ...queryDebounce });

  const stockItems = flatInfiniteResult(data);

  useEffect(() => {
    if (itemSelectedIds.length && open) {
      setSelectItems((prevState) => [
        ...prevState,
        ...stockItems
          .filter(
            (item) => !prevState.some((state) => state.partId === item.partId),
          )
          .filter((item) =>
            allParts
              ? !itemSelectedIds.includes(item?.partId)
              : itemSelectedIds.includes(item?.partId),
          ),
      ]);
    }
  }, [itemSelectedIds, dataUpdatedAt, allParts, open]);

  function handleOrder(sorter: SorterResult<StockItem>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (prev.orderColumns === StockOrderColumnConfig[sorter.columnKey]) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: StockOrderColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const columns: ColumnsType<StockItem> = [
    useConfigurationColumnsPart({ isSku: true }),
    {
      key: PartItemFieldOption.Manufacturer,
      ellipsis: true,
      dataIndex: ['manufacturer', 'name'],
      title: 'Fabricante',
      width: 200,
      sorter: true,
    },
    {
      key: PartItemFieldOption.Description,
      ellipsis: true,
      dataIndex: 'description',
      title: 'Descrição',
      width: 300,
      sorter: true,
    },
  ];

  const handleCancel = (submitAll: 'single' | 'all' | null = null) => {
    if (!allParts && submitAll === null && !itemSelectedIds.length) {
      setQueryParams({
        itemType: StockItemType.Part,
        order: 'ASC',
        active: true,
      });
      setAllSelecteds(false);
    }

    if (allParts && !allSelecteds && submitAll !== 'single') {
      setAllSelecteds(true);
    }

    onCancel();
  };

  const handleOk = async () => {
    if (allSelecteds) {
      const result = await listStockItems.mutateAsync({
        ...queryParams,
        search: undefined,
        rows: 100000,
      });

      if (result?.result.data.length) {
        onCallback(
          result?.result.data.filter(
            (item) =>
              !selectItems?.some(
                (itemSelected) => itemSelected.partId === item.partId,
              ),
          ) || [],
          allSelecteds,
        );

        handleCancel('all');
      }
    } else {
      onCallback(selectItems, allSelecteds);
      handleCancel('single');
    }
  };

  return (
    <Modal
      {...props}
      width={800}
      open={open}
      title="Seleção de peças"
      onOk={handleOk}
      okText={itemSelectedIds.length ? 'Atualizar peças' : 'Incluir peças'}
      okButtonProps={{ disabled: !allSelecteds && !selectItems?.length }}
      onCancel={() => handleCancel()}
      confirmLoading={listStockItems.isLoading}
      cancelButtonProps={{ disabled: listStockItems.isLoading }}
      closable={!listStockItems.isLoading}
    >
      {listStockItems.isLoading && (
        <ModalLoadingOverlay messages={overlayMessages} />
      )}

      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        extraFilters={
          <PartsExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            inModal
          />
        }
      />

      {allSelecteds && (
        <Alert
          type="warning"
          description="Os campos de filtros influênciam na criação das etiquetas."
          style={{ marginBottom: 24 }}
        />
      )}

      {enableAllSelects && (
        <Group position="between" style={{ marginBottom: 24 }}>
          <div>
            {allSelecteds && (
              <strong>
                Clique no <i>checkbox</i> para remover a seleção
              </strong>
            )}
          </div>

          <Checkbox
            checked={allSelecteds}
            onChange={({ target }) => {
              setAllSelecteds(target.checked);
              setSelectItems([]);
            }}
          >
            Todas as peças
          </Checkbox>
        </Group>
      )}

      <InfiniteTable
        columns={columns}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrder(sorter);
          }
        }}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        dataSource={stockItems}
        loading={isFetching}
        rowKey={(row) => row.partId}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: allSelecteds
            ? stockItems
                ?.filter(
                  (item) =>
                    !selectItems
                      .map((item) => item?.partId)
                      .includes(item?.partId),
                )
                .map((item) => item?.partId)
            : selectItems.map((item) => item?.partId),
          getCheckboxProps: (item) => ({
            disabled:
              disableWithChecked &&
              itemSelectedIds?.some(
                (itemSelectedId) => itemSelectedId === item?.partId,
              ),
          }),
          onChange: (keys, rows, info) => {
            if (allSelecteds) {
              if (info.type !== 'all') {
                const itemsRemoved = stockItems.filter(
                  (item) => !keys.includes(item?.partId),
                );

                setSelectItems((prevState) => [
                  ...prevState
                    .filter((state) => !keys.includes(state.partId))
                    .filter(
                      (state) =>
                        !itemsRemoved.some(
                          (item) => item.partId === state.partId,
                        ),
                    ),

                  ...itemsRemoved,
                ]);
              }
            } else {
              setSelectItems(rows);
            }
          },
        }}
      />
    </Modal>
  );
};

export default SelectPartsListModal;
